import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useToast } from '@/components/Toast';
import { AuthLayout } from './AuthLayout';
import {
  MIN_PASSWORD_LENGTH as MIN_LENGTH,
  isPasswordAcceptable,
  passwordProblems,
} from '@/lib/password';

/**
 * The forced password change after an admin created the account with a password.
 *
 * This exists because the convenience of admin-set passwords has a cost: for a while,
 * two people know the credential. Routing the user here until they replace it keeps
 * that window as short as possible, which is what makes the feature acceptable rather
 * than a standing shared secret.
 *
 * Also reachable voluntarily, so it doubles as the ordinary "change my password" screen.
 */
export function ChangePasswordPage() {
  const { me, refresh, signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const forced = me?.user.mustChangePassword === true;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One shared policy with the server, so the button cannot enable for something the
  // API will reject — including the blocked-fragment rule this screen used to omit.
  const problems = useMemo(
    () => passwordProblems(newPassword, { currentPassword, confirmation: confirm }),
    [currentPassword, newPassword, confirm],
  );

  const canSubmit =
    currentPassword.length > 0 &&
    isPasswordAcceptable(newPassword, { currentPassword, confirmation: confirm }) &&
    !submitting;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/password/change', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });

      // Re-read the session so `mustChangePassword` clears and the guard releases.
      await refresh();
      toast.success('Password updated.');
      navigate('/', { replace: true });
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : 'Something went wrong. Please try again.',
      );
      setCurrentPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title={forced ? 'Choose your own password' : 'Change your password'}
      subtitle={
        forced
          ? 'Your account was set up with a password chosen by an admin. Replace it with one only you know.'
          : 'Pick something you have not used before.'
      }
      footer={
        // No "back" link while forced — there is nowhere else to go. Signing out is the
        // only alternative, and it must stay reachable so nobody is trapped.
        forced ? (
          <button
            type="button"
            onClick={() => void signOut()}
            className="font-medium text-muted hover:text-ink"
          >
            Sign out instead
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="font-medium text-accent hover:underline"
          >
            Back
          </button>
        )
      }
    >
      {error && (
        <div
          className="mb-5 rounded-control border border-[#e0796b]/45 bg-[#e0796b]/12 px-3.5 py-3 text-sm text-ink"
          role="alert"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-4">
          <label className="label" htmlFor="current-password">
            {forced ? 'The password you were given' : 'Current password'}
          </label>
          <input
            id="current-password"
            type="password"
            className="input"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <div className="mb-4">
          <label className="label" htmlFor="new-password">
            New password
          </label>
          <input
            id="new-password"
            type="password"
            className="input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
            aria-describedby="new-password-rules"
          />
          <p id="new-password-rules" className="mt-1.5 text-xs text-muted">
            At least {MIN_LENGTH} characters, including a letter and a number.
          </p>
        </div>

        <div className="mb-4">
          <label className="label" htmlFor="confirm-password">
            Confirm new password
          </label>
          <input
            id="confirm-password"
            type="password"
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {problems.length > 0 && (
          <ul className="mb-4 space-y-1" aria-live="polite">
            {problems.map((problem) => (
              <li key={problem} className="text-xs text-secondary">
                • {problem}
              </li>
            ))}
          </ul>
        )}

        <button type="submit" disabled={!canSubmit} className="btn-primary w-full">
          {submitting ? 'Saving…' : 'Update password'}
        </button>
      </form>
    </AuthLayout>
  );
}
